import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRouteSnapshot, Resolve } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { StoreStateServiceService } from '../services/store-state-service/store-state-service.service';
import { EngineCommonService } from '@zen/engine/services/engine-common.service';

/**
 * Interface representing the shape of the resolved page data.
 */
interface PageData {
  components?: ComponentConfig[];
}

/**
 * Interface describing the configuration of a dynamic component.
 */
interface ComponentConfig {
  selector: string;
  displayOrder: number;
  componentPath: string;
  componentName: string;
  [key: string]: any;
}

/**
 * Angular resolver that loads page component configuration before route activation.
 */
@Injectable({ providedIn: 'root' })
export class RouteResolver implements Resolve<PageData> {
  /** Injected HTTP client for loading JSON configuration. */
  private readonly http = inject(HttpClient);

  /** Injected service to fetch current user token/data. */
  private readonly tokenService = inject(EngineCommonService);

  /** Injected store service for managing global application state. */
  private readonly store = inject(StoreStateServiceService);

  /** In-memory cache for resolved JSON responses, keyed by route pageKey. */
  private readonly jsonCache = new Map<string, Observable<PageData>>();

  /** Base path where page configuration files are hosted. */
  private readonly configBasePath = 'market-place/json';
  private readonly platformId = inject(PLATFORM_ID); 

  /** Common headers for JSON HTTP requests. */
  private readonly jsonHeaders = new HttpHeaders({
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  });

  /**
   * Resolves component configuration data before navigating to the route.
   * @param route The current route snapshot containing route data.
   * @returns Observable containing resolved and processed PageData.
   */
  resolve(route: ActivatedRouteSnapshot): Observable<PageData> {
    const pageKey = route.data['pageKey'] || 'default';
    const qaParam = route.queryParamMap.get('dev') ?? 'false';
    this.store.set('qaMode', qaParam === 'true');
    // Return cached observable if available to prevent refetching
    if (this.jsonCache.has(pageKey)) {
      return this.jsonCache.get(pageKey)!;
    }

    const configUrl = `${this.configBasePath}/${pageKey}.json`;

    // Request and process config file, then cache result
    const request$ = this.http.get<PageData>(configUrl, {
      headers: this.jsonHeaders,
      responseType: 'json'
    }).pipe(
      map(response => this.processResponse(response)),
      catchError(error => this.handleError(error)),
      switchMap((pageData) => this.ensureCurrentUserLoaded(qaParam).pipe(
        map(() => pageData)
      )),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.jsonCache.set(pageKey, request$); // Cache the result
    return request$;
  }

  /**
   * Normalizes and sorts the component configuration response.
   * @param response Raw API response
   * @returns Processed and sorted page data.
   */
  private processResponse(response: unknown): PageData {
    if (Array.isArray(response)) {
      // Flatten array of items and extract component lists
      return {
        components: this.sortComponents(
          response.flatMap(item => item.components || [])
        )
      };
    }

    if (response && typeof response === 'object' && 'components' in response) {
      // If already in PageData shape, sort components
      return {
        ...response,
        components: this.sortComponents((response as PageData).components || [])
      };
    }

    // Fallback for invalid or empty responses
    return { components: [] };
  }

  /**
   * Sorts components by display order.
   * @param components Array of unsorted component configs.
   * @returns Sorted array of component configs.
   */
  private sortComponents(components: ComponentConfig[]): ComponentConfig[] {
    return [...components].sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Handles failed HTTP requests and logs the error.
   * @param error Error object from HTTP failure.
   * @returns Fallback empty PageData observable.
   */
  private handleError(error: unknown): Observable<PageData> {
    console.error('RouteResolver error:', error);
    return of({ components: [] });
  }

  /**
   * Caches the current user in the global store if not already present.
   */
  private ensureCurrentUserLoaded(qaParam: string): Observable<unknown> {
    if (qaParam === 'true') {
      return of(null);
    }

    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const storeUser = this.store.getValue<any>('getUser');
    if (this.hasCurrentUser(storeUser)) {
      return of(storeUser);
    }

    const cachedUser = this.getCurrentUserFromStorage() as any;
    if (this.hasCurrentUser(cachedUser)) {
      this.store.set('getUser', cachedUser?.getCurrentUser ? cachedUser : { getCurrentUser: cachedUser });
      return of(cachedUser);
    }

    if (!localStorage.getItem('jwt_token') && this.store.getSignal('qamode')) {
      return of(null);
    }

    return this.cacheUserData();
  }

  /**
   * Checks whether the given payload contains a usable current user.
   * @param user User payload from store, storage, or API.
   * @returns True when current user details are available.
   */
  private hasCurrentUser(user: any): boolean {
    return Boolean(user?.getCurrentUser?.currentUser || user?.currentUser);
  }

  /**
   * Reads any previously saved current user payload from local storage.
   * @returns Parsed current user payload or null when unavailable.
   */
  private getCurrentUserFromStorage(): unknown {
    // ✅ localStorage doesn't exist in Node
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const storedUser = localStorage.getItem('getCurrentUser');
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch (error) {
      console.error('Failed to parse getCurrentUser from localStorage:', error);
      localStorage.removeItem('getCurrentUser');
      return null;
    }
  }

  /**
   * Fetches the current user and stores it for later route/component access.
   * @returns Observable that completes after user data is stored.
   */
  private cacheUserData(): Observable<unknown> {
    const region = isPlatformBrowser(this.platformId) ? localStorage.getItem('region') ?? '' : '';

    const data = {
      input: {
        query: {
          region: region
        }
      }
    };
    return this.tokenService.getCurrentUser(data).pipe(
      tap(user => {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.setItem('getCurrentUser', JSON.stringify(user?.getCurrentUser));
        }
        this.store.set('getUser', user);
      }),
      catchError(error => {
        console.error('Failed to fetch current user in RouteResolver:', error);
        const localUser = this.getCurrentUserFromStorage() as  any;
        if (this.hasCurrentUser(localUser)) {
          this.store.set('getUser', localUser?.getCurrentUser ? localUser : { getCurrentUser: localUser });
          return of(localUser);
        }
        return of(null);
      })
    );
  }
}
